import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { RefreshCw } from 'lucide-react';
import { lazy, Suspense, useId, useState, type ReactNode } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useConfig } from '@/shared/config';
import { notifyApiError, useNotifier } from '@/shared/data';
import { DateFormField, DocumentFormField, SelectFormField, TextFormField, useConfirm } from '@/shared/form';
import { isValidCpf } from '@/shared/format/cpf';
import { formatDocument, stripDocumentSeparators } from '@/shared/format/document';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';

import { getZetraMargin, marginDocument, requestZetraMargin, zetraWrite, type MarginWrite, type ZetraMargin } from '../credit-note.margin';
import type { CreditNote } from '../credit-note.wire';
import { validDocument } from './margin-dataprev';
import { useZetraMarginOptions } from './margin-options';
import { MarginCards, MarginPanel } from './margin-panel';
import { applyMarginWrite, formProductId } from './margin-write';
import { useTabsForm } from './tab-form';
import { kovrWarrantyDeadline, legacyIsoWithTimezone } from './tab-warranty-form';

// "Consultar › Margem" do Zetra (MarginQueryFormContainer.tsx com `isZetra`), uma fonte só para a Operação
// (creditNoteActions.tsx:1146-1166) e a Simulação livre (SimulationFormContainer.tsx:239-246, pela porta): CPF e matrícula
// → margem guardada (`GET`, sem custo) → opção da operação, prazo de averbação e margens → garantias de consignado público
// **somadas** às atuais (SelectWarrantyContainer.tsx:234-273), por `onApply`. "Obter/Atualizar margem": com tomador, a
// consulta nova (paga) depois da prévia (U53, U24); sem tomador, o `UpdateZetra` com os tokens do servidor. Fora do agente.

// O `UpdateZetra` (R5), sob demanda.
const ZetraUpdateSheet = /* @__PURE__ */ lazy(() => import('./margin-zetra-update').then((module) => ({ default: module.ZetraUpdateSheet })));

interface QueryValues {
  registrationNumber: string;
  employeeCode: string;
}

interface SelectValues {
  warrantyOption: string;
  warrantyDeadline: string | null;
  previousAdeNumber: string | null;
}

/** O prazo de averbação de quem abre: o inicial (`defaultvaluesSelectWarrantyFormZetra`), o dia dele e o do envio. */
export interface ZetraDeadlines {
  /** O instante inicial (hoje + 40 dias; com `IS_KOVR`, + 30). */
  readonly initial: string;
  /** O dia civil do inicial, no campo. */
  readonly initialCivil: string;
  /** O `warrantyDeadline` que vai na garantia, pelo dia escolhido (`resolveWarrantyDeadline`, helpers:17-20). */
  readonly resolve: (civil: string | null) => string;
}

export interface ZetraMarginSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** O CPF (cru); só vale com tomador (MarginQueryForm.tsx:36-48). */
  readonly initialRegistration: string;
  readonly personId: string | null | undefined;
  readonly productId: string | null | undefined;
  readonly kovr: boolean;
  /** As garantias atuais de quem abriu: a escolha soma a elas. */
  readonly currentWarranty: readonly unknown[];
  readonly deadlines: ZetraDeadlines;
  /** A Operação aceita CPF ou CNPJ (`cpfCnpjValidation`); a Simulação, só CPF. */
  readonly acceptCnpj?: boolean;
  /** A consulta nova paga com tomador (a Operação e a Simulação montam a query de jeitos diferentes: ver o relatório da onda 4). */
  readonly requestPaid: (input: { readonly personId: string; readonly creditProductId: string | null | undefined }) => Promise<unknown>;
  readonly onApply: (write: MarginWrite) => void;
}

const STEP_QUERY = { id: 'form', title: msg`Consulta` };
const STEP_SELECT = { id: 'select', title: msg`Margens` };

export function ZetraMarginSheet({ open, onOpenChange, initialRegistration, personId, productId, kovr, currentWarranty, deadlines, acceptCnpj = false, requestPaid, onApply }: ZetraMarginSheetProps) {
  const { t } = useLingui();
  const notifier = useNotifier();
  const [confirm, confirmElement] = useConfirm();
  const queryFormId = useId();
  const selectFormId = useId();
  const initialDocument = personId ? initialRegistration : '';
  const [phase, setPhase] = useState<'form' | 'select'>('form');
  const [document, setDocument] = useState(initialDocument);
  const [employeeCode, setEmployeeCode] = useState('');
  const [margin, setMargin] = useState<ZetraMargin | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [querying, setQuerying] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);

  const valid = (value: string) => (acceptCnpj ? validDocument(stripDocumentSeparators(value)) : isValidCpf(stripDocumentSeparators(value)));
  const querySchema = z.object({
    registrationNumber: z
      .string()
      .refine((value) => value.trim() !== '', t`CPF: precisa ser preenchido.`)
      .refine((value) => value.trim() === '' || valid(value), t`CPF é inválido.`),
    employeeCode: z.string(),
  });
  const queryForm = useForm<QueryValues>({ resolver: zodResolver(querySchema), defaultValues: { registrationNumber: initialDocument, employeeCode: '' }, mode: 'onTouched' });
  const typed = useWatch({ control: queryForm.control, name: 'registrationNumber' });
  const locked = initialDocument !== '' || valid(typed);

  // PublicPayrollSchema.ts:6-19.
  const selectSchema = z
    .object({ warrantyOption: z.string(), warrantyDeadline: z.string().nullable(), previousAdeNumber: z.string().nullable() })
    .superRefine((values, context) => {
      // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
      if (!['None', 'FreeMargin', 'Portability'].includes(values.warrantyOption)) context.addIssue({ code: 'custom', path: ['warrantyOption'], message: t`Selecione a opção de garantia.` });
      if (values.warrantyOption === 'Portability' && (values.previousAdeNumber ?? '').trim() === '') context.addIssue({ code: 'custom', path: ['previousAdeNumber'], message: t`Número do ADE anterior: precisa ser preenchido.` });
    });
  // eslint-disable-next-line lingui/no-unlocalized-strings -- valor técnico do fio (U02), não texto de UI
  const selectForm = useForm<SelectValues>({ resolver: zodResolver(selectSchema), defaultValues: { warrantyOption: 'FreeMargin', warrantyDeadline: deadlines.initialCivil, previousAdeNumber: null }, mode: 'onTouched' });
  const option = useWatch({ control: selectForm.control, name: 'warrantyOption' });

  const close = () => onOpenChange(false);
  const options = useZetraMarginOptions(margin);

  /** A margem guardada (`zetraMarginQueryRefecth`); o erro vira toast e o painel fica com o aviso de sem dados (zetra/index.ts:236-238). */
  const load = async (registrationNumber: string, code: string) => {
    try {
      const list = await getZetraMargin({ registrationNumber, employeeCode: code });
      setMargin(list[0]);
    } catch (error) {
      setMargin(undefined);
      notifyApiError(notifier, error);
    }
  };

  const submitQuery = async (values: QueryValues) => {
    const cpf = stripDocumentSeparators(values.registrationNumber);
    const code = values.employeeCode.replace(/\s/g, '');
    setDocument(cpf);
    setEmployeeCode(code);
    setSelected([]);
    setPhase('select');
    setBusy(true);
    try {
      await load(cpf, code);
    } finally {
      setBusy(false);
    }
  };

  /** `handleMarginQuery` do Zetra (MarginQueryFormContainer.tsx:778-784) e `onSuccessZetra` (:294-302). */
  const requestMargin = async () => {
    // Sem tomador, o "Atualizar margem" com a matrícula e os tokens (`openDrawerZetra`; `UpdateZetra`, :708-718).
    if (!personId) {
      setUpdateOpen(true);
      return;
    }
    const formatted = formatDocument(document) ?? document;
    const ok = await confirm({
      title: t`Nova consulta de margem`,
      description: t`A consulta da margem livre do CPF ${formatted} no Zetra é paga: cada nova consulta é cobrada e substitui a margem guardada.`,
      confirmLabel: t`Sim, consultar`,
      tone: 'default',
    });
    if (!ok) return;
    setQuerying(true);
    try {
      await requestPaid({ personId, creditProductId: productId });
      notifier.notify({ tone: 'success', title: t`Margem atualizada com sucesso!`, description: t`Ótimo! Agora você pode selecionar umas das opções de margem disponíveis.` });
      await load(document, employeeCode);
    } catch (error) {
      notifyApiError(notifier, error);
    } finally {
      setQuerying(false);
    }
  };

  const confirmSelection = (values: SelectValues) => {
    if (selected.length === 0) {
      notifier.notify({ tone: 'warning', title: t`Selecione ao menos 1 modelo de margem` });
      return;
    }
    const write = zetraWrite(
      margin,
      selected,
      { warrantyOption: values.warrantyOption, warrantyDeadline: deadlines.resolve(values.warrantyDeadline), previousAdeNumber: values.previousAdeNumber },
      document,
      currentWarranty as Parameters<typeof zetraWrite>[4],
    );
    // helpers:149-152 e SelectWarrantyContainer.tsx:264-272: quem abriu escreve e o painel fecha (nada vai ao servidor).
    onApply(write);
    close();
  };

  const footer: ReactNode =
    phase === 'form' ? (
      <Button type="submit" form={queryFormId} loading={busy}>
        {t`Confirmar`}
      </Button>
    ) : (
      <>
        <Button type="button" variant="secondary" loading={querying} disabled={busy} onClick={() => void requestMargin()}>
          <RefreshCw aria-hidden />
          {margin ? t`Atualizar margem` : t`Obter margem`}
        </Button>
        <Button type="submit" form={selectFormId}>
          {t`Confirmar`}
        </Button>
      </>
    );

  return (
    <>
      <MarginPanel
        open={open && !updateOpen}
        onOpenChange={onOpenChange}
        title={phase === 'form' ? t`Consultar margem` : t`Selecione uma das margens disponíveis`}
        steps={[STEP_QUERY, STEP_SELECT]}
        current={phase}
        onBack={phase === 'select' ? () => setPhase('form') : undefined}
        busy={querying}
        footer={footer}
      >
        {phase === 'form' ? (
          <FormProvider {...queryForm}>
            <form
              id={queryFormId}
              noValidate
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void queryForm.handleSubmit(submitQuery)();
              }}
            >
              <DocumentFormField<QueryValues> name="registrationNumber" label={t`CPF`} required disabled={locked} placeholder={t`000.000.000-00`} />
              <TextFormField<QueryValues> name="employeeCode" label={t`Matrícula`} />
            </form>
          </FormProvider>
        ) : (
          <FormProvider {...selectForm}>
            <form
              id={selectFormId}
              noValidate
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void selectForm.handleSubmit(confirmSelection)();
              }}
            >
              {margin || busy ? null : <Alert tone="info">{t`Não há dados de margem disponíveis para esse CPF. Clique no botão 'Obter margem' para fazer a primeira consulta`}</Alert>}
              {/* PublicPayrollForm.tsx:26-55. */}
              <SelectFormField<SelectValues>
                name="warrantyOption"
                label={t`Opção de operação`}
                required
                options={[
                  // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
                  { value: 'FreeMargin', label: t`Margem Livre` },
                  // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
                  { value: 'Portability', label: t`Portabilidade` },
                ]}
              />
              <DateFormField<SelectValues> name="warrantyDeadline" label={t`Data limite de averbação`} disabled={kovr} />
              {option === 'Portability' ? <TextFormField<SelectValues> name="previousAdeNumber" label={t`Nº ADE anterior`} required /> : null}
              <MarginCards label={t`Margens disponíveis`} options={options} selected={selected} onToggle={(key, checked) => setSelected((current) => (checked ? [...current, key] : current.filter((item) => item !== key)))} />
            </form>
          </FormProvider>
        )}
      </MarginPanel>
      {/* `UpdateZetra` com o CPF da consulta, o produto e a matrícula (MarginQueryFormContainer.tsx:708-718); no sucesso,
          toast e releitura (`onSuccessZetra`, :294-302). */}
      <Suspense fallback={null}>
        <ZetraUpdateSheet
          open={open && updateOpen}
          onOpenChange={setUpdateOpen}
          registrationNumber={document}
          employeeCode={employeeCode === '' ? null : employeeCode}
          creditProductId={productId}
          onUpdated={() => load(document, employeeCode)}
        />
      </Suspense>
      {confirmElement}
    </>
  );
}

/** `defaultvaluesSelectWarrantyFormZetra.warrantyDeadline` da Operação (SelectWarrantySchema.ts:15-21): hoje + 40 dias, na hora de agora. */
function operationDeadlines(now: Date, kovr: boolean): ZetraDeadlines {
  const next = new Date(now);
  next.setDate(next.getDate() + 40);
  const initial = kovr ? kovrWarrantyDeadline(now) : legacyIsoWithTimezone(next);
  const initialCivil = initial.slice(0, 10);
  return {
    initial,
    initialCivil,
    // O `date()` do yup vira instante em UTC no JSON; vazio, `resolveWarrantyDeadline` usa agora (helpers:17-20).
    resolve: (civil) => (kovr ? kovrWarrantyDeadline(new Date()) : civil === null || civil === '' ? legacyIsoWithTimezone(new Date()) : new Date(civil === initialCivil ? initial : `${civil}T00:00:00.000-03:00`).toISOString()),
  };
}

/** "Consultar › Margem" da Operação: o formulário da operação dá o CPF, o produto e as garantias e recebe a escolha. */
export function ZetraMarginQuery({ note, open, onOpenChange }: { readonly note: CreditNote; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const detail = useTabsForm();
  const config = useConfig() as Record<string, unknown>;
  const kovr = Boolean(config['IS_KOVR']);
  const [deadlines] = useState(() => operationDeadlines(new Date(), kovr));
  return (
    <ZetraMarginSheet
      open={open}
      onOpenChange={onOpenChange}
      // MarginQueryForm.tsx:36-48: com tomador, o CPF da operação (travado); sem tomador, em branco.
      initialRegistration={marginDocument(note)}
      personId={note.personId}
      productId={formProductId(detail)}
      kovr={kovr}
      currentWarranty={detail.getValues('warranty')}
      deadlines={deadlines}
      acceptCnpj
      requestPaid={requestZetraMargin}
      onApply={(write) => applyMarginWrite(detail, write)}
    />
  );
}
